import {useEffect, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {Combobox, ComboboxOption} from '@/components/ui/combobox'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {IconText} from '@/components/shared/IconText'
import {Plus, X as XIcon} from 'lucide-react'
import type {InitialOwnerRequest} from '@/types/owner'
import type {LocalizedTypeValue} from '@/hooks/use-app-config'

interface OwnersPickerProps {
    owners: InitialOwnerRequest[]
    onOwnersChange: (owners: InitialOwnerRequest[]) => void
    responsibilityTypes: LocalizedTypeValue[]
    /** Caller-owned cache of party id → display name. The picker fills this as it
     * sees parties in search results so the Review screen can resolve labels
     * for ids that aren't on the current search page. */
    ownerLabels: Record<string, string>
    onOwnerLabelsChange: (labels: Record<string, string>) => void
}

/**
 * Multi-row owner picker shared across catalog create wizards
 * (Function Group, AutoAssign Rule Request, Restriction Rule Request).
 *
 * Renders one row per owner: party Combobox + responsibility Select + remove button.
 * An Add row button is rendered below.
 *
 * Validation lives at the caller — every row's {@code ownerPartyId} and
 * {@code responsibility} must be non-empty for the form to be considered valid.
 */
export function OwnersPicker({
    owners,
    onOwnersChange,
    responsibilityTypes,
    ownerLabels,
    onOwnerLabelsChange,
}: OwnersPickerProps) {
    const services = useServices()
    const i18n = useI18n()
    const [partySearchQuery, setPartySearchQuery] = useState('')
    const {data: partySearchData, isLoading: partiesLoading} = useQuery({
        queryKey: ['parties', 'search', partySearchQuery],
        queryFn: () => services.parties.list({q: partySearchQuery, size: 10}),
    })

    useEffect(() => {
        const incoming = partySearchData?.content
        if (!incoming?.length) return
        const next: Record<string, string> = {...ownerLabels}
        let changed = false
        for (const p of incoming) {
            if (next[p.id] !== p.derivedDisplayName) {
                next[p.id] = p.derivedDisplayName
                changed = true
            }
        }
        if (changed) onOwnerLabelsChange(next)
    }, [partySearchData, ownerLabels, onOwnerLabelsChange])

    const partyOptions: ComboboxOption[] = (partySearchData?.content || []).map(p => ({
        value: p.id,
        label: p.derivedDisplayName,
        description: p.partyType,
    }))

    const updateOwner = (idx: number, patch: Partial<InitialOwnerRequest>) =>
        onOwnersChange(owners.map((r, i) => (i === idx ? {...r, ...patch} : r)))
    const addOwner = () => {
        // eslint-disable-next-line i18next/no-literal-string -- responsibility is a backend type code
        const defaultResponsibility = responsibilityTypes[0]?.code ?? 'BUSINESS'
        onOwnersChange([...owners, {ownerPartyId: '', responsibility: defaultResponsibility}])
    }
    const removeOwner = (idx: number) => onOwnersChange(owners.filter((_, i) => i !== idx))

    return (
        <Stack gap="sm" data-testid="owners-editor">
            {owners.map((o, idx) => (
                <InlineRow key={idx} gap="sm" data-testid={`owner-row-${idx}`}>
                    <Combobox
                        options={partyOptions}
                        value={o.ownerPartyId || null}
                        onValueChange={v => updateOwner(idx, {ownerPartyId: v ?? ''})}
                        onSearch={setPartySearchQuery}
                        placeholder={i18n.common.search}
                        emptyMessage={i18n.common.noResults}
                        isLoading={partiesLoading}
                        selectedLabel={ownerLabels[o.ownerPartyId]}
                        data-testid={`combobox-owner-party-${idx}`}
                    />
                    <Select
                        value={o.responsibility}
                        onValueChange={v => updateOwner(idx, {responsibility: v})}
                    >
                        <SelectTrigger data-testid={`select-owner-responsibility-${idx}`}>
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            {responsibilityTypes.map(t => (
                                <SelectItem
                                    key={t.code}
                                    value={t.code}
                                    data-testid={`owner-responsibility-${idx}-${t.code}`}
                                >
                                    {t.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {owners.length > 1 && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            data-testid={`btn-remove-owner-${idx}`}
                            onClick={() => removeOwner(idx)}
                            aria-label={i18n.common.remove}
                        >
                            <XIcon className="h-4 w-4"/>
                        </Button>
                    )}
                </InlineRow>
            ))}
            <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="btn-add-owner"
                onClick={addOwner}
            >
                <IconText icon={Plus}>{i18n.owners.addOwner}</IconText>
            </Button>
        </Stack>
    )
}

/**
 * Helper to render a join'd list of owner display names for review screens.
 * Falls back to the raw partyId when a label hasn't been resolved.
 */
export function formatOwnerNames(
    owners: InitialOwnerRequest[],
    labels: Record<string, string>,
    fallback = '—',
): string {
    if (owners.length === 0) return fallback
    return owners.map(o => labels[o.ownerPartyId] ?? o.ownerPartyId).join(', ')
}

/**
 * Validation helper — true when every owner row carries a partyId + responsibility.
 */
export function areOwnersValid(owners: InitialOwnerRequest[]): boolean {
    return owners.length > 0
        && owners.every(o => o.ownerPartyId.length > 0 && o.responsibility.length > 0)
}
